import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { toast } from 'sonner';
import { LoginCredentials, BasicRegisterData } from '../types/auth';

interface User {
  id: string;
  name: string;
  email: string;
  hasCompletedProfile: boolean;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'REGISTER_SUCCESS'; payload: User }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_PROFILE_STATUS'; payload: boolean };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'UPDATE_PROFILE_STATUS':
      return {
        ...state,
        user: state.user ? { ...state.user, hasCompletedProfile: action.payload } : null,
      };
    default:
      return state;
  }
}

const initialState: AuthState = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
};

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (data: BasicRegisterData) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateProfileStatus: (status: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const checkAuth = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        const res = await fetch('/api/me', {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data) {
            dispatch({ type: 'LOGIN_SUCCESS', payload: data.data });
          } else {
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };
    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
        credentials: 'include',
      });
      const data = await res.json();
      
      if (data.success && data.data) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: data.data });
        toast.success('Acesso autorizado', {
          style: { background: '#121212', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff' },
        });
        return true;
      } else {
        toast.error(data.error || 'Credenciais inválidas', {
          style: { background: '#121212', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff' },
        });
        return false;
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor', {
        style: { background: '#121212', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff' },
      });
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const register = async (data: BasicRegisterData): Promise<{ ok: boolean; error?: string }> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'include',
      });
      const response = await res.json();
      
      if (response.success && response.data) {
        dispatch({ type: 'REGISTER_SUCCESS', payload: response.data });
        toast.success('Conta criada com sucesso', {
          style: { background: '#121212', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff' },
        });
        return { ok: true };
      } else {
        toast.error(response.error || 'Falha ao registrar', {
          style: { background: '#121212', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff' },
        });
        return { ok: false, error: response.error || 'Falha ao registrar' };
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor', {
        style: { background: '#121212', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff' },
      });
      return { ok: false, error: 'Erro ao conectar com o servidor' };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const logout = async () => {
    await fetch('/api/logout', { 
      method: 'POST',
      credentials: 'include',
    });
    dispatch({ type: 'LOGOUT' });
    toast.info('Sessão encerrada', {
      style: { background: '#121212', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff' },
    });
  };

  const updateProfileStatus = (status: boolean) => {
    dispatch({ type: 'UPDATE_PROFILE_STATUS', payload: status });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        updateProfileStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
